import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Users, TrendingUp, MessageCircle, Calendar, Sun, Moon, MapPin, Shield, Lock, ShieldCheck, Eye, UserCheck, Globe } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';

const Landing = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <nav className="px-6 md:px-12 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-heading font-black text-gray-900 dark:text-white">Socyy</h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-all theme-toggle"
            data-testid="landing-theme-toggle"
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <Link to="/login">
            <Button variant="ghost" className="rounded-full font-bold text-gray-700 dark:text-gray-200" data-testid="landing-login-btn">
              Login
            </Button>
          </Link>
          <Link to="/register">
            <Button className="rounded-full bg-primary hover:bg-primary-700 text-white font-bold px-6 shadow-lg hover:scale-105 transition-transform" data-testid="landing-signup-btn">
              Sign Up
            </Button>
          </Link>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-heading font-black tracking-tighter mb-6 text-gray-900 dark:text-white" data-testid="landing-hero-title">
            Social Media,
            <br />
            <span className="text-gradient">But Actually Fun</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-10 max-w-3xl mx-auto font-medium" data-testid="landing-hero-subtitle">
            Connect, create, and discover what's happening around you.
            Find local events, trending venues, and vibrant communities with Locale.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => navigate('/register')}
              className="rounded-full bg-gradient-to-r from-primary to-accent hover:scale-105 text-white font-bold px-10 py-6 text-lg shadow-xl shadow-primary/30 transition-transform"
              data-testid="landing-get-started-btn"
            >
              Get Started Free
            </Button>
            <Button
              onClick={() => navigate('/pricing')}
              variant="outline"
              className="rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white font-bold px-10 py-6 text-lg transition-all"
              data-testid="landing-view-pricing-btn"
            >
              Go Premium
            </Button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20"
        >
          {[
            { icon: MapPin, title: 'Locale', desc: 'Discover real events, venues & local businesses near you', color: 'from-rose-500 to-pink-600' },
            { icon: TrendingUp, title: 'Analytics', desc: 'Track growth & engagement insights', color: 'from-accent to-accent-600' },
            { icon: Calendar, title: 'Schedule Posts', desc: 'Plan content ahead of time', color: 'from-secondary to-secondary-600' },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="bg-white/70 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
              data-testid={`feature-card-${idx}`}
            >
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                <feature.icon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-heading font-bold mb-2 text-gray-900">{feature.title}</h3>
              <p className="text-gray-600">{feature.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-20"
          data-testid="trust-badges-section"
        >
          <p className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-8">
            Trusted & Secure
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
            {[
              { icon: Lock, label: 'SSL Encrypted', color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
              { icon: Shield, label: 'Content Moderated', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' },
              { icon: Eye, label: 'Screen Capture Protected', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' },
              { icon: UserCheck, label: 'Identity Verified Users', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800' },
              { icon: ShieldCheck, label: 'GDPR Compliant', color: 'text-teal-600 dark:text-teal-400', bg: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800' },
              { icon: Globe, label: 'Community Driven', color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800' },
            ].map((badge, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: idx * 0.08 }}
                viewport={{ once: true }}
                className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl border ${badge.bg} shadow-sm`}
                data-testid={`trust-badge-${idx}`}
              >
                <badge.icon className={`w-5 h-5 ${badge.color}`} />
                <span className={`text-sm font-semibold ${badge.color}`}>{badge.label}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* AI Ethics Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-20 max-w-4xl mx-auto"
        >
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-3xl p-8 md:p-12 border border-emerald-200 dark:border-emerald-800">
            <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                  <span className="text-4xl">🤝</span>
                </div>
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                  Humans First. Always.
                </h3>
                <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-4">
                  We believe AI should <span className="font-semibold text-emerald-600 dark:text-emerald-400">empower people</span>, not replace them. 
                  Our tools are designed to assist your creativity, protect your rights, and keep you in control. 
                  No copyrighted content. No job replacement. Just technology that respects humanity.
                </p>
                <div className="flex flex-wrap justify-center md:justify-start gap-3">
                  <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium">
                    ©️ Copyright Respectful
                  </span>
                  <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-sm font-medium">
                    ⚖️ DMCA Compliant
                  </span>
                  <span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 rounded-full text-sm font-medium">
                    🔒 Privacy First
                  </span>
                  <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-sm font-medium">
                    👤 Human Oversight
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-4 text-gray-900">Ready to join Socyy?</h2>
          <p className="text-lg text-gray-600 mb-8">Start free, upgrade when you need more power</p>
          <Link to="/register">
            <Button className="rounded-full bg-gradient-to-r from-primary via-accent to-secondary hover:scale-105 text-white font-bold px-12 py-6 text-lg shadow-xl transition-transform" data-testid="landing-join-now-btn">
              Join Now - It's Free
            </Button>
          </Link>
        </div>
      </div>

      <footer className="bg-white/50 dark:bg-gray-900/50 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-600 dark:text-gray-400 text-center md:text-left">© {new Date().getFullYear()} Socyy. All rights reserved.</p>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/legal/privacy" className="text-gray-600 dark:text-gray-400 hover:text-primary font-medium transition-colors">
                Privacy Policy
              </Link>
              <Link to="/legal/terms" className="text-gray-600 dark:text-gray-400 hover:text-primary font-medium transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;